import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
import get from 'lodash.get';
import removeCartItem from './remove-cart-item';
import { onFetchFailure, clearCartState } from './action-creators';
import removePromotionCode from './remove-promotion-code';
import mergeCarts from './merge-carts';
import { getCartItems } from '../selectors';

// Map of error codes that cause us to remove the product from the user's cart:
var notBuyableCodes = {
  PRODUCT_NOT_BUYABLE: true,
  OUT_OF_STOCK: true
};
var quantityLimitCodes = {
  ITEM_QUANTITY_LIMIT: true
};
var promoCodeErrorCodes = {
  PROMOTION_INVALID: true,
  PROMOTION_EXPIRED: true
};

/**
 * @ignore
 * @returns {Promise} result of handling cart errors
 */
export default function handleCartErrors(_ref) {
  var service = _ref.service,
    error = _ref.error,
    params = _ref.params;
  return function (dispatch, getState) {
    // 1. destructure errors and data from the error out of the catch block where this is called
    var errors = error.errors,
      data = error.data;
    // 2. check for errors
    if (errors && errors.length) {
      var _errors$, _getCartItems;
      // 3. reduce over errors that have fields
      var itemsToRemove = errors.filter(function (e) {
        return e.field;
      }).reduce(function (accum, curr) {
        // 4. check codes to see which errors need to be worked on
        var shouldFilterProduct = notBuyableCodes[curr.code] || quantityLimitCodes[curr.code] || promoCodeErrorCodes[curr.code];
        // 5. from the field string target the item by skuId eg:'item/0/skuId' | '/promotionCodes/0'
        var filteredField = get(data, curr.field.split('/').filter(function (x) {
          return x;
        }).join('.'));
        // 6. if filtered field is a number
        var isANumber = !isNaN(filteredField);
        if (isANumber) {
          // 7. from the field string target the line item id eg:'item/0/quantity'
          var lineItemId = curr.field.split('/').filter(function (x) {
            return x;
          }).join('.').replace('quantity', 'id');
          var shouldAdjustQuantity = get(data, lineItemId);

          // 8. if theres an error to be addressed
          return shouldFilterProduct ? _objectSpread(_objectSpread({}, accum), {}, {
            reduceQuantity: _objectSpread(_objectSpread({}, accum.reduceQuantity), {}, _defineProperty({}, shouldAdjustQuantity, true))
          }) : accum;
        }

        // 10. if filtered field is not a number and there's an error to be addressed
        if (shouldFilterProduct) {
          if (promoCodeErrorCodes[curr.code]) {
            return _objectSpread(_objectSpread({}, accum), {}, {
              invalidPromoCodes: [].concat(_toConsumableArray(accum.invalidPromoCodes), [filteredField // 11. accumulate invalid promoCodes here
              ])
            });
          }

          return _objectSpread(_objectSpread({}, accum), {}, {
            notBuyableSkus: _objectSpread(_objectSpread({}, accum.notBuyableSkus), {}, _defineProperty({}, filteredField, true))
          });
        }
        return accum;
      }, {
        invalidPromoCodes: [],
        notBuyableSkus: {},
        reduceQuantity: {}
      });

      // 13. determine if cart has any items over limit or has any non buyable items
      var hasOverLimitItems = Object.keys(itemsToRemove.reduceQuantity).length > 0;
      var hasNonBuyableItems = Object.keys(itemsToRemove.notBuyableSkus).length > 0;
      var hasInvalidPromoCodes = itemsToRemove.invalidPromoCodes.length > 0;

      // 14. if there are invalid promos only
      if (hasInvalidPromoCodes && !hasNonBuyableItems && !hasOverLimitItems) {
        return dispatch(removePromotionCode(_objectSpread(_objectSpread({}, params), {}, {
          promotionCodes: itemsToRemove.invalidPromoCodes
        }))).then(
        // 16. and in all cases afterwards, propagate the errors up
        function () {
          return errors.map(function (err) {
            var message = err.message,
              crcCode = err.crcCode,
              code = err.code,
              field = err.field;
            return dispatch(onFetchFailure({
              service: service,
              error: new Error(message),
              code: code,
              crcCode: crcCode,
              data: data,
              field: field
            }));
          });
        });
      }

      // 17. if has items over limit but not non-buyable nor invalid promos
      if (hasOverLimitItems && !hasNonBuyableItems && !hasInvalidPromoCodes) {
        // 18. use mergeCarts with validateOnly flag to force the server to fix the cart
        return dispatch(mergeCarts(_objectSpread(_objectSpread({}, params), {}, {
          validateOnly: true
        }))).then(
        // 19. propagate the errors up
        function () {
          return errors.forEach(function (err) {
            var message = err.message,
              crcCode = err.crcCode,
              code = err.code,
              field = err.field;
            return dispatch(onFetchFailure({
              service: service,
              error: new Error(message),
              code: code,
              crcCode: crcCode,
              data: data,
              field: field
            }));
          });
        });
      }

      // 20. if hasNonBuyableItems but not over limit items nor invalid promoCodes
      if (hasNonBuyableItems && !hasOverLimitItems && !hasInvalidPromoCodes) {
        var items = get(data, 'items', []).filter(function (item) {
          return itemsToRemove.notBuyableSkus[item.skuId];
        });
        if (items.length) {
          // 21. remove offending items
          return dispatch(removeCartItem(_objectSpread(_objectSpread({}, params), {}, {
            items: items
          }))).then(
          // 22. propagate the errors up
          function () {
            return errors.forEach(function (err) {
              var message = err.message,
                crcCode = err.crcCode,
                code = err.code,
                field = err.field;
              return dispatch(onFetchFailure({
                service: service,
                error: new Error(message),
                code: code,
                crcCode: crcCode,
                data: data,
                field: field,
                itemsRemoved: items
              }));
            });
          });
        }
      }

      // 23. if both non-buyable and over limit but no invalid promos
      if (hasNonBuyableItems && hasOverLimitItems && !hasInvalidPromoCodes) {
        var _items = get(data, 'items', []).filter(function (item) {
          return itemsToRemove.notBuyableSkus[item.skuId];
        });

        // 24. remove offending items
        return dispatch(removeCartItem(_objectSpread(_objectSpread({}, params), {}, {
          items: _items
        }))).then(function () {
          return (
            // 25. use mergeCarts with validateOnly flag to force the server to fix the cart
            dispatch(mergeCarts(_objectSpread(_objectSpread({}, params), {}, {
              validateOnly: true
            }))).then(
            // 26. propagate the errors up
            function () {
              return errors.forEach(function (err) {
                var message = err.message,
                  crcCode = err.crcCode,
                  code = err.code,
                  field = err.field;
                return dispatch(onFetchFailure({
                  service: service,
                  error: new Error(message),
                  code: code,
                  crcCode: crcCode,
                  data: data,
                  field: field,
                  itemsRemoved: _items
                }));
              });
            })
          );
        });
      }

      // 27. if has non buyable item & invalid promos but no over limit items
      if (hasNonBuyableItems && !hasOverLimitItems && hasInvalidPromoCodes) {
        var _items2 = get(data, 'items', []).filter(function (item) {
          return itemsToRemove.notBuyableSkus[item.skuId];
        });

        // 28. first remove items
        return dispatch(removeCartItem(_objectSpread(_objectSpread({}, params), {}, {
          items: _items2
        }))).then(
        // 29. then replace items
        function () {
          return dispatch(removePromotionCode(_objectSpread(_objectSpread({}, params), {}, {
            promotionCodes: itemsToRemove.invalidPromoCodes
          }))).then(function () {
            return errors.forEach(function (err) {
              var message = err.message,
                crcCode = err.crcCode,
                code = err.code,
                field = err.field;
              // 30. propagate the errors up
              return dispatch(onFetchFailure({
                service: service,
                error: new Error(message),
                code: code,
                crcCode: crcCode,
                data: data,
                field: field,
                itemsRemoved: _items2
              }));
            });
          });
        });
      }

      // 31. if has over limit item & invalid promos
      if (!hasNonBuyableItems && hasOverLimitItems && hasInvalidPromoCodes) {
        // 32. use mergeCarts with validateOnly flag to force the server to fix the cart
        return dispatch(mergeCarts(_objectSpread(_objectSpread({}, params), {}, {
          validateOnly: true
        }))).then(
        // 33. then remove promo codes
        function () {
          return dispatch(removePromotionCode(_objectSpread(_objectSpread({}, params), {}, {
            promotionCodes: itemsToRemove.invalidPromoCodes
          }))).then(
          // 34. then propagate the errors up
          function () {
            return errors.forEach(function (err) {
              var message = err.message,
                crcCode = err.crcCode,
                code = err.code,
                field = err.field;
              return dispatch(onFetchFailure({
                service: service,
                error: new Error(message),
                code: code,
                crcCode: crcCode,
                data: data,
                field: field
              }));
            });
          });
        });
      }

      // 35. if has non-buyable, over limit and invalid promos
      if (hasNonBuyableItems && hasOverLimitItems && hasInvalidPromoCodes) {
        var _items3 = get(data, 'items', []).filter(function (item) {
          return itemsToRemove.notBuyableSkus[item.skuId];
        });
        // 36. first remove items
        return dispatch(removeCartItem(_objectSpread(_objectSpread({}, params), {}, {
          items: _items3
        }))).then(function () {
          return (
            // 37. use mergeCarts with validateOnly flag to force the server to fix the cart
            dispatch(mergeCarts(_objectSpread(_objectSpread({}, params), {}, {
              validateOnly: true
            })))
            // 38. then remove promo codes
            .then(function () {
              return dispatch(removePromotionCode(_objectSpread(_objectSpread({}, params), {}, {
                promotionCodes: itemsToRemove.invalidPromoCodes
              })));
            })
            // 39. then propagate the errors up
            .then(function () {
              return errors.forEach(function (err) {
                var message = err.message,
                  crcCode = err.crcCode,
                  code = err.code,
                  field = err.field;
                return dispatch(onFetchFailure({
                  service: service,
                  error: new Error(message),
                  crcCode: crcCode,
                  code: code,
                  data: data,
                  field: field,
                  itemsRemoved: _items3
                }));
              });
            })
          );
        });
      }

      // 40. Handle invalid session when navigating through user journey. Clearing previous state for empty cart.
      if ((errors === null || errors === void 0 ? void 0 : (_errors$ = errors[0]) === null || _errors$ === void 0 ? void 0 : _errors$.status) === 404 && ((_getCartItems = getCartItems(getState())) === null || _getCartItems === void 0 ? void 0 : _getCartItems.length) > 0) {
        dispatch(clearCartState());
      }
      return errors.forEach(function (err) {
        var message = err.message,
          crcCode = err.crcCode,
          code = err.code,
          field = err.field;
        // 41. if we have errors after handling the above cases afterwards, propagate them up
        return dispatch(onFetchFailure({
          service: service,
          error: new Error(message),
          code: code,
          crcCode: crcCode,
          data: data,
          field: field
        }));
      });
    }

    // 42. if no errors, then propagate the entire error up
    return dispatch(onFetchFailure({
      service: service,
      error: error,
      data: data
    }));
  };
}